import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  Chip,
  IconButton,
  Alert,
  Grid,
  Card,
  CardContent,
  FormHelperText
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';

const PaymentAccountManager = () => {
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfig, setShowConfig] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    processor_type: '',
    config: {},
    is_active: true
  });

  const processorTypes = [
    { value: 'stripe', label: 'Stripe' },
    { value: 'eway', label: 'eWay' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'square', label: 'Square' }
  ];

  const processorConfigs = {
    stripe: [
      { key: 'publishableKey', label: 'Publishable Key', type: 'text', required: true },
      { key: 'secretKey', label: 'Secret Key', type: 'password', required: true }
    ],
    eway: [
      { key: 'apiKey', label: 'API Key', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
      { key: 'publicApiKey', label: 'Public API Key (for PayNow button)', type: 'text', required: false },
      { key: 'sandbox', label: 'Sandbox Mode', type: 'boolean', required: false }
    ],
    paypal: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { key: 'sandbox', label: 'Sandbox Mode', type: 'boolean', required: false }
    ],
    square: [
      { key: 'applicationId', label: 'Application ID', type: 'text', required: true },
      { key: 'accessToken', label: 'Access Token', type: 'password', required: true },
      { key: 'locationId', label: 'Location ID', type: 'text', required: false },
      { key: 'sandbox', label: 'Sandbox Mode', type: 'boolean', required: false }
    ]
  };

  useEffect(() => {
    fetchPaymentAccounts();
  }, []);

  const fetchPaymentAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/payment-accounts');
      if (!response.ok) throw new Error('Failed to fetch payment accounts');
      const data = await response.json();
      setPaymentAccounts(data);
    } catch (err) {
      setError('Failed to load payment accounts');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (account = null) => {
    if (account) {
      setEditingAccount(account);
      
      // Parse config if it's a string (from API)
      let parsedConfig = account.config;
      if (typeof account.config === 'string') {
        try {
          parsedConfig = JSON.parse(account.config);
        } catch (error) {
          console.error('Error parsing config JSON:', error);
          parsedConfig = {};
        }
      }
      
      // Clean config data - filter out numeric keys from corrupted data
      let cleanConfig = {};
      if (parsedConfig && typeof parsedConfig === 'object') {
        cleanConfig = Object.fromEntries(
          Object.entries(parsedConfig).filter(([key]) => isNaN(key))
        );
        
        // Handle field name mapping for backwards compatibility
        if (cleanConfig.public_api_key && !cleanConfig.publicApiKey) {
          cleanConfig.publicApiKey = cleanConfig.public_api_key;
          delete cleanConfig.public_api_key;
        }
        if (cleanConfig.api_key && !cleanConfig.apiKey) {
          cleanConfig.apiKey = cleanConfig.api_key;
          delete cleanConfig.api_key;
        }
      }
      
      const formDataToSet = {
        name: account.name,
        processor_type: account.processor_type,
        config: cleanConfig,
        is_active: account.is_active
      };
      
      setFormData(formDataToSet);
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        processor_type: '',
        config: {},
        is_active: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingAccount(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      const url = editingAccount 
        ? `/api/admin/payment-accounts/${editingAccount.id}`
        : '/api/admin/payment-accounts';
      
      const method = editingAccount ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save payment account');
      }

      await fetchPaymentAccounts();
      handleClose();
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment account?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/admin/payment-accounts/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete payment account');
      }

      await fetchPaymentAccounts();
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'processor_type') {
      // Reset config when processor type changes
      setFormData({
        ...formData,
        [name]: value,
        config: {}
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  const handleConfigChange = (key, value, type) => {
    const newValue = type === 'boolean' ? value : value;
    const updatedFormData = {
      ...formData,
      config: {
        ...formData.config,
        [key]: newValue
      }
    };
    
    setFormData(updatedFormData);
  };

  const toggleConfigVisibility = (accountId, field) => {
    setShowConfig(prev => ({
      ...prev,
      [`${accountId}-${field}`]: !prev[`${accountId}-${field}`]
    }));
  };

  const renderConfigFields = () => {
    if (!formData.processor_type) return null;

    const fields = processorConfigs[formData.processor_type] || [];

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {processorTypes.find(p => p.value === formData.processor_type)?.label} Configuration
        </Typography>
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid item xs={12} sm={6} key={field.key}>
              {field.type === 'boolean' ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.config[field.key] || false}
                      onChange={(e) => handleConfigChange(field.key, e.target.checked, 'boolean')}
                    />
                  }
                  label={field.label}
                />
              ) : (
                <TextField
                  fullWidth
                  label={field.label}
                  type={field.type}
                  value={formData.config[field.key] || ''}
                  onChange={(e) => handleConfigChange(field.key, e.target.value, field.type)}
                  required={field.required}
                  variant="outlined"
                />
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderConfigDisplay = (config, accountId) => {
    // Handle malformed config data
    if (!config || typeof config !== 'object') {
      return (
        <Typography variant="caption" color="error">
          Invalid configuration
        </Typography>
      );
    }

    // Check if config contains numeric keys (corrupted data)
    const hasNumericKeys = Object.keys(config).some(key => !isNaN(key));
    if (hasNumericKeys) {
      return (
        <Typography variant="caption" color="error">
          Configuration corrupted - needs repair
        </Typography>
      );
    }

    // Filter out numeric keys and handle field name mapping for display
    let displayConfig = Object.fromEntries(
      Object.entries(config).filter(([key]) => isNaN(key))
    );
    
    // Handle backwards compatible field names for display
    if (displayConfig.public_api_key && !displayConfig.publicApiKey) {
      displayConfig.publicApiKey = displayConfig.public_api_key;
      delete displayConfig.public_api_key;
    }
    if (displayConfig.api_key && !displayConfig.apiKey) {
      displayConfig.apiKey = displayConfig.api_key;
      delete displayConfig.api_key;
    }

    const validConfig = Object.entries(displayConfig);
    
    if (validConfig.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No configuration
        </Typography>
      );
    }

    return (
      <Box>
        {validConfig.map(([key, value]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" sx={{ minWidth: 80 }}>
              {key}:
            </Typography>
            <Typography variant="caption" sx={{ ml: 1, fontFamily: 'monospace' }}>
              {typeof value === 'boolean' 
                ? (value ? 'Yes' : 'No')
                : maskSensitiveValue(value, showConfig[`${accountId}-${key}`])
              }
            </Typography>
            {typeof value === 'string' && (key.toLowerCase().includes('key') || key.toLowerCase().includes('secret') || key.toLowerCase().includes('token') || key.toLowerCase().includes('password')) ? (
              <IconButton
                size="small"
                onClick={() => toggleConfigVisibility(accountId, key)}
                sx={{ ml: 1 }}
              >
                {showConfig[`${accountId}-${key}`] ? <VisibilityOffIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
              </IconButton>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  };

  const getProcessorLabel = (type) => {
    return processorTypes.find(p => p.value === type)?.label || type;
  };

  const maskSensitiveValue = (value, show) => {
    if (!value) return '';
    if (show) return value;
    return '••••••••••••';
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Payment Accounts
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage payment processor accounts that can be assigned to practitioners.
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ mb: 3 }}
        >
          Add Payment Account
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Processor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assignments</TableCell>
                <TableCell>Configuration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentAccounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {account.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getProcessorLabel(account.processor_type)}
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={account.is_active ? 'Active' : 'Inactive'}
                      color={account.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {account.practitionerAssignments?.length || 0} practitioners
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {renderConfigDisplay(account.config, account.id)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleOpen(account)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(account.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {paymentAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No payment accounts found. Create your first payment account to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingAccount ? 'Edit Payment Account' : 'Add Payment Account'}
          </DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Account Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  helperText="Descriptive name for this payment account"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Processor Type</InputLabel>
                  <Select
                    name="processor_type"
                    value={formData.processor_type}
                    onChange={handleChange}
                    label="Processor Type"
                  >
                    {processorTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Payment processor for this account</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>

            {renderConfigFields()}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingAccount ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default PaymentAccountManager;